import { AddCircleOutlineIcon, VisibilityIcon } from '@weblite/icons'
import clsx from 'clsx'
import { Button, Typography, alpha, styled } from '@mui/material'
import { blackToTransparentGradient } from '$setup/style/helpers'
import { t } from '$i18n'
import { formatViews } from '$func/text'
import { isNumber } from '$helper/functions/utils'

export function ActionButtons({
  views,
  isPeerHall,
  onCreateStory,
  openViewersList,
  hidden,
  isStoryAdmin,
}: {
  views: number | undefined
  onCreateStory: () => void
  openViewersList: () => void
  hidden?: boolean
  isStoryAdmin: boolean
  isPeerHall: boolean
}) {
  const formattedViews = formatViews(views || 0)
  return (
    <>
      <Backdrop className={clsx({ hidden })} />
      {isStoryAdmin ? (
        <ViewButton
          className={clsx({ hidden })}
          onClick={openViewersList}
          data-test-id="peer-story-viewer-viewers-list-button"
        >
          <View formattedViews={formattedViews} views={views} />
        </ViewButton>
      ) : isPeerHall ? null : null}
      {isStoryAdmin && (
        <AddStoryButton
          className={clsx({ hidden })}
          startIcon={
            <AddCircleOutlineIcon sx={{ width: '24px', height: '24px' }} />
          }
          onClick={onCreateStory}
          data-test-id="peer-story-viewer-add-story-button"
        >
          <Typography color="inherit" variant="subtitle3">
            {t('story.viewStoryModal.addStory')}
          </Typography>
        </AddStoryButton>
      )}
    </>
  )
}

function View({
  views,
  formattedViews,
  className,
}: {
  views: number | undefined
  formattedViews:
    | { shortenedViews: string; postfix?: undefined }
    | { shortenedViews: string; postfix: string }
  className?: string
}) {
  return isNumber(views) ? (
    <ViewBox className={className}>
      <VisibilityIcon
        sx={{
          color: 'neutral000',
          width: '20px',
          height: '20px',
        }}
      />
      <Typography color="inherit" variant="subtitle1">
        {`${formattedViews.shortenedViews} ${formattedViews.postfix || ''}`}
      </Typography>
    </ViewBox>
  ) : null
}

const TRANSITION = 'opacity 0.15s ease'
const Backdrop = styled('div')`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 72px;
  background: ${blackToTransparentGradient('top')};
  z-index: 1;
  transition: ${TRANSITION};
  pointer-events: none;
  &.hidden {
    opacity: 0;
  }
`

const AddStoryButton = styled(Button)`
  color: ${({ theme }) => theme.palette.neutral000};
  border-radius: 12px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.palette.storyGradientH};
  box-sizing: border-box;
  position: absolute;
  bottom: 16px;
  right: 12px;
  z-index: 2;
  transition: ${TRANSITION};
  &.hidden {
    opacity: 0;
  }
`

const ViewBox = styled('div')`
  border: 1px solid ${({ theme }) => alpha(theme.palette.neutral000, 0.1)};
  background: linear-gradient(
    1.45deg,
    rgba(255, 255, 255, 0.1) 1.25%,
    rgba(255, 255, 255, 0.15) 98.79%
  );
  box-shadow: 0 10px 20px 0 #0000001a;
  color: ${({ theme }) => theme.palette.neutral000};
  border-radius: 16px;
  .MuiButton-startIcon {
    margin-left: 8px;
  }
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 64px;
  height: 48px;
  transition: ${TRANSITION};
  &.hidden {
    opacity: 0;
  }
  &.absolute {
    position: absolute;
    left: 12px;
    bottom: 16px;
    z-index: 2;
  }
`

const ViewButton = styled(Button)`
  width: 64px;
  box-sizing: border-box;
  height: 48px;
  padding: 0;
  position: absolute;
  left: 12px;
  bottom: 16px;
  z-index: 2;
  transition: ${TRANSITION};
  &.hidden {
    opacity: 0;
  }
`
